/**
 * Transport HTTP unique du frontend.
 *
 * Toutes les pages parlent au backend par ce module (via les fonctions typées de
 * `services/api/*.ts`), jamais par `fetch` directement. Il concentre ce que chaque
 * appel répétait à la main : le cookie de session, l'en-tête JSON, la lecture de
 * l'enveloppe `ApiResponse` du paquet `shared`, la transformation d'un échec en
 * `ApiError`, et la détection d'une session expirée (401) à un seul endroit.
 *
 * Contrat backend : toute réponse est `{ success, data?, error?, message?, details? }`.
 * - `success: true`  → la valeur utile est `data` (parfois absente : suppression, ping…).
 * - `success: false` ou statut HTTP hors 2xx → `ApiError` avec le statut, le message
 *   du serveur (`error` puis `message`) et les `details` de validation s'il y en a.
 * - Erreur réseau → `ApiError` de statut 0 ; délai dépassé → statut 0 aussi, message dédié.
 *
 * Session expirée : un 401 sur une route `/api/…` autre que `/api/auth/*` (qui gèrent
 * leur propre 401 : mauvais mot de passe, identité initiale, invitation…) appelle le
 * gestionnaire posé par `UserProvider` (`setSessionExpiredHandler`), qui vide l'utilisateur
 * et renvoie sur la page de connexion. La réponse est ensuite rendue à l'appelant.
 */
import type { ApiResponse } from '@unity-learning/shared';
import { APP_VERSION_HEADER, noteServedVersion } from '../../utils/appVersion';

export type ApiErrorPayload = ApiResponse<unknown> & { details?: unknown };

export class ApiError extends Error {
  /** Statut HTTP ; 0 pour une erreur réseau ou un délai dépassé. */
  readonly status: number;
  /** Détails de validation renvoyés par le serveur (liste de messages), s'il y en a. */
  readonly details: string[];
  /** Enveloppe brute renvoyée par le serveur, si elle a pu être lue. */
  readonly payload?: ApiErrorPayload;

  constructor(status: number, message: string, payload?: ApiErrorPayload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
    this.details = Array.isArray(payload?.details)
      ? payload!.details.filter((d): d is string => typeof d === 'string')
      : [];
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Message affichable pour n'importe quelle erreur attrapée autour d'un appel API. */
export function errorMessage(error: unknown, fallback = 'Une erreur est survenue'): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export const NETWORK_ERROR_MESSAGE = 'Erreur de connexion au serveur. Veuillez réessayer.';
export const TIMEOUT_ERROR_MESSAGE = 'La connexion prend trop de temps. Vérifiez votre réseau.';

const AUTH_PATH_PREFIX = '/api/auth/';

let sessionExpiredHandler: (() => void) | null = null;

/** Posé par `UserProvider` : appelé à chaque 401 hors `/api/auth/*`. */
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  sessionExpiredHandler = handler;
}

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  /** Paramètres de requête ; les valeurs `undefined`/`null` sont omises. */
  query?: Record<string, QueryValue>;
  /** Annulation par l'appelant (préchargement abandonné, composant démonté…). */
  signal?: AbortSignal;
  /** Délai maximal en millisecondes ; au-delà, `ApiError` de statut 0. */
  timeoutMs?: number;
  /** La requête part même si l'utilisateur navigue aussitôt (analytics). */
  keepalive?: boolean;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

/**
 * Appel brut : renvoie l'enveloppe complète (`success`, `data`, `message`…).
 * Lève `ApiError` si le statut n'est pas 2xx ou si `success` est faux.
 */
export async function apiRequest<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const url = buildUrl(path, options.query);

  // Délai : un contrôleur interne relié au signal de l'appelant s'il y en a un.
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;
  let signal = options.signal;
  if (options.timeoutMs && options.timeoutMs > 0) {
    const controller = new AbortController();
    if (options.signal) {
      if (options.signal.aborted) controller.abort();
      else options.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
    timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, options.timeoutMs);
    signal = controller.signal;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials: 'include',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
      keepalive: options.keepalive,
    });
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      if (timedOut) throw new ApiError(0, TIMEOUT_ERROR_MESSAGE);
      throw error; // annulation voulue par l'appelant : on la laisse passer telle quelle
    }
    throw new ApiError(0, NETWORK_ERROR_MESSAGE);
  }
  if (timeoutId) clearTimeout(timeoutId);

  noteServedVersion(response.headers?.get(APP_VERSION_HEADER));

  if (response.status === 401 && path.startsWith('/api/') && !path.startsWith(AUTH_PATH_PREFIX)) {
    sessionExpiredHandler?.();
  }

  let payload: ApiErrorPayload | undefined;
  try {
    payload = (await response.json()) as ApiErrorPayload;
  } catch {
    payload = undefined; // corps vide (204) ou non JSON (page d'erreur du proxy)
  }

  if (!response.ok || !payload || payload.success !== true) {
    const message =
      payload?.error ||
      payload?.message ||
      (response.ok ? 'Réponse inattendue du serveur' : `Erreur ${response.status}: ${response.statusText || 'requête refusée'}`);
    throw new ApiError(response.status, message, payload);
  }

  return payload as ApiResponse<T>;
}

/** GET : renvoie `data` typé. */
export async function apiGet<T>(path: string, options?: RequestOptions): Promise<T> {
  return (await apiRequest<T>('GET', path, undefined, options)).data as T;
}

/** POST : renvoie `data` typé (`void` pour les routes qui n'en renvoient pas). */
export async function apiPost<T = void>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return (await apiRequest<T>('POST', path, body ?? {}, options)).data as T;
}

export async function apiPut<T = void>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return (await apiRequest<T>('PUT', path, body ?? {}, options)).data as T;
}

export async function apiDelete<T = void>(path: string, options?: RequestOptions): Promise<T> {
  return (await apiRequest<T>('DELETE', path, undefined, options)).data as T;
}
